import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';

import { Alert, CircularProgress } from '@mui/material';
import { ChatEditorMarkdownPlugin } from 'src/components/ai/chat/ChatPanel/ChatEditor';
import type { Apollo } from 'src/gql/generated';

import { SkillMarkdownEditor } from './skill-markdown-editor';
import type { SkillMarkdownEditorRef } from './skill-markdown-editor';
import {
  decodeSkillAttachmentBytes,
  useSkillAttachmentContent,
} from './use-skill-attachment-content';

export interface SkillTextFileEditorRef {
  /** Report an edit still waiting in the rich-text editor's debounce now. */
  flush: () => void;
}

const SKILL_TEXT_FILE_PLUGINS = [ChatEditorMarkdownPlugin];
const MARKDOWN_EDITOR_STYLE: React.CSSProperties = {
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
};
const MARKDOWN_TEXTAREA_STYLE: React.CSSProperties = { fontSize: 14, lineHeight: 1.6, padding: 24 };

/**
 * A Markdown or plain text file of a skill, in the rich-text editor. A disabled editor never
 * reports a change, so a viewer opening a file cannot leave the page with unsaved edits.
 */
export const SkillTextFileEditor = React.memo(
  forwardRef<
    SkillTextFileEditorRef,
    {
      disabled: boolean;
      onChange: (path: string, content: string) => void;
      path: string;
      skill: Apollo.AgentSkillSimpleFragment;
      /** The unsaved text, when the file has been edited; otherwise the stored file is loaded. */
      content?: string;
      generation?: number;
    }
  >(function SkillTextFileEditor(
    { content, disabled, generation = 0, onChange, path, skill },
    ref
  ) {
    const markdownEditorRef = useRef<SkillMarkdownEditorRef>(null);
    const displayedTextRef = useRef<{ generation: number; path: string; text: string } | null>(
      null
    );
    const {
      content: stored,
      error,
      loading,
    } = useSkillAttachmentContent({ path, skill, skip: content !== undefined });
    const storedText = useMemo(
      () => stored && new TextDecoder().decode(decodeSkillAttachmentBytes(stored.base64)),
      [stored]
    );
    // While the stored file refetches, the text last shown for this file stays on screen.
    const displayed = displayedTextRef.current;
    const hasDisplayedText = displayed?.generation === generation && displayed.path === path;
    const text = content ?? storedText ?? (hasDisplayedText ? displayed.text : '');
    if (content !== undefined || stored) displayedTextRef.current = { generation, path, text };

    const changeMarkdown = useCallback(
      (markdown: string) => {
        if (!disabled) onChange(path, markdown);
      },
      [disabled, onChange, path]
    );

    useImperativeHandle(ref, () => ({ flush: () => markdownEditorRef.current?.flush() }), []);

    if (loading && !hasDisplayedText) return <CircularProgress size={24} />;
    if (error)
      return <Alert severity="error">Could not load this file. Try opening it again.</Alert>;
    return (
      <SkillMarkdownEditor
        key={`${path}:${generation}`}
        ref={markdownEditorRef}
        noBorder
        disabled={disabled}
        extraPlugins={SKILL_TEXT_FILE_PLUGINS}
        maxHeight="none"
        minHeight={400}
        style={MARKDOWN_EDITOR_STYLE}
        textareaStyle={MARKDOWN_TEXTAREA_STYLE}
        value={text}
        onChange={changeMarkdown}
      />
    );
  })
);
